import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@reach/router";

import useQuery from "hooks/useQuery";
import Icon from "./PlanIcon";
import { PLAN_HIGHLIGHTS, PLAN_TAGLINES, planIncludes } from "./content";
import { planName, planTheme } from "./planDisplay";
import styles from "./Planes.module.scss";

// Ítems visibles antes de "Ver más" (en el diseño se corta en los de cobros).
const PREVIEW_ITEMS = 8;

/**
 * Panel "Estás contratando <plan>" del formulario de suscripción (card 1053, caso 5
 * "link de pago"): se muestra cuando la URL es `/suscripcion/<plan>`, el link que
 * reenvía Ventas.
 * Sin precio: el de la cuenta lo confirma Ventas.
 *
 * El catálogo (`GET /plans`) es el mismo que usa la pantalla de Planes; un código que no
 * está en el catálogo no muestra nada y el formulario sigue como siempre.
 */
export default function PlanSummary({ code }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const catalog = useQuery("plans", { staleTime: 60000, retry: false });

  const plans = [...(catalog.data?.plans || [])].sort((a, b) => a.rank - b.rank);
  const index = plans.findIndex((plan) => plan.code === code);
  if (index < 0) return null;

  const plan = plans[index];
  const previous = plans[index - 1] ?? null;
  const tagline = PLAN_TAGLINES[plan.code];
  const intro = PLAN_HIGHLIGHTS[plan.code]?.intro;
  const sections = planIncludes(plan, previous);
  const total = sections.reduce((sum, section) => sum + section.items.length, 0);

  let shown = 0;
  const visible = sections
    .map((section) => {
      if (expanded) return section;
      const room = Math.max(0, PREVIEW_ITEMS - shown);
      const items = section.items.slice(0, room);
      shown += items.length;
      return { ...section, items };
    })
    .filter((section) => section.items.length);

  const itemText = ([key, value, opts]) =>
    opts?.feeKey ? t(key, value, { fee: t(opts.feeKey[0], opts.feeKey[1]) }) : t(key, value, opts);

  return (
    <aside className={`${styles.summary} ${planTheme(plan.code)}`} data-testid="plan-summary">
      <div className={styles.summaryHead}>
        <p className={styles.summaryEyebrow}>{t("planes_summary_eyebrow", "Estás contratando")}</p>
        <p className={styles.summaryName}>{planName(t, plan)}</p>
        {tagline && <p className={styles.tagline}>{t(tagline[0], tagline[1])}</p>}
      </div>

      <div className={styles.summaryBody}>
        <p className={styles.summaryEyebrow}>{t("planes_summary_includes", "Qué incluye")}</p>
        {previous && intro && <p className={styles.intro}>{t(intro[0], intro[1])}</p>}
        {visible.map((section) => (
          <div key={section.title[0]} className={styles.summarySection}>
            <p className={styles.summarySectionTitle}>{t(section.title[0], section.title[1])}</p>
            <ul className={styles.highlights}>
              {section.items.map((item) => (
                <li key={item.label[0]}>
                  <Icon name={item.ai ? "auto_awesome" : "check"} size={16} className={item.ai ? styles.ai : styles.yes} />
                  <span>
                    {itemText(item.label)}
                    {item.extra && <span className={styles.extra}> {t(item.extra[0], item.extra[1])}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {total > PREVIEW_ITEMS && (
          <button type="button" className={styles.inlineButton} onClick={() => setExpanded((value) => !value)}>
            {expanded ? t("planes_summary_less", "Ver menos ↑") : t("planes_summary_more", "Ver más ↓")}
          </button>
        )}
      </div>

      <div className={styles.summaryFoot}>
        <p>{t("planes_summary_price_note", "El precio para tu cuenta es el que te confirmó el equipo de ventas.")}</p>
        <Link to={window._l("/planes")} className={styles.summaryLink}>
          {t("planes_summary_all_plans", "Ver todos los planes y qué incluye cada uno →")}
        </Link>
      </div>
    </aside>
  );
}
